import React, { useMemo, useEffect, useState, useRef } from "react";
import T from '../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
// 🟢 NEW CONTEXT IMPORT
import { useWorkforce } from "../../context/WorkforceContext"; 
import { useTasks } from "../../context/TaskContext"; 
import { useProjects } from "../../context/ProjectContext"; 
import { formatDate } from "../../utils/dateUtils";

// Icons
import { 
  BriefcaseIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  ArrowLeftIcon,
  UserCircleIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon
} from "@heroicons/react/24/outline";

export default function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loadedIdRef = useRef(null);
  
  // 🟢 SWITCH TO WORKFORCE CONTEXT
  const { getWorkforceById } = useWorkforce();
  const { tasks } = useTasks();
  const { projects } = useProjects();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🟢 ASYNC FETCH FOR WORKFORCE DATA
  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    const fetchEmployee = async () => {
        try {
            setLoading(true);
            const data = await getWorkforceById(id);
            setEmployee(data);
        } catch (error) {
            console.error("Failed to load employee details", error);
        } finally {
            setLoading(false);
        }
    };

    fetchEmployee();
  }, [id, getWorkforceById]);

  // 🟢 CALCULATE METRICS (Updated field names)
  const workData = useMemo(() => {
    if (!employee) return { assignedTasks: [], projectCount: 0, completedTasks: 0 };

    // Filter tasks by ID or Name (using 'name' from new DB structure)
    const assignedTasks = tasks.filter(t => 
        String(t.employeeId) === String(id) || 
        t.assignedTo?.toLowerCase() === employee.name?.toLowerCase()
    );

    const completedTasks = assignedTasks.filter(t => t.status === "Completed").length;
    const uniqueProjectIds = new Set(assignedTasks.map(t => t.projectId));
    const activeProjects = projects.filter(p => uniqueProjectIds.has(String(p.id)));

    return { assignedTasks, projectCount: activeProjects.length, completedTasks, activeProjects };
  }, [employee, tasks, projects, id]);

  const handleAssignTask = () => {
    navigate("/admin/task/add", { state: { prefillEmployeeId: employee.id } });
  };

  if (loading) return <div className="p-10 text-center text-gray-500"><T>Loading employee profile...</T></div>;
  if (!employee) return <div className="p-10 text-center text-red-500"><T>Employee not found</T></div>;

  return (
    <>
      <div className="p-6 bg-gray-50 dark:bg-[#0d1117] min-h-screen font-sans">
        
        {/* HEADER */}
        <div className="flex justify-between items-start mb-8">
            <div>
                <button 
                    onClick={() => navigate("/admin/EmployeePage")} 
                    className="flex items-center text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white mb-2 transition"
                >
                    <ArrowLeftIcon className="w-4 h-4 mr-1" /> Back to Workforce
                </button>
                <div className="flex items-center gap-4">
                    <div className="h-16 w-16 bg-blue-100 dark:bg-blue-950/60 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 text-2xl font-bold shadow-inner">
                        {/* 🟢 USE 'name' */}
                        {employee.name ? employee.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white">{employee.name}</h1>
                        <p className="text-gray-500 dark:text-slate-400">{employee.role} • {employee.department || "General"}</p>
                        {/* 🟢 SHOW EMPLOYEE ID */}
                        <p className="text-xs text-gray-400 dark:text-slate-500 font-mono mt-1">ID: {employee.employee_id}</p>
                    </div>
                </div>
            </div>
            <div className="flex gap-2">
               <button className="bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-200 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-[#21262d] text-sm shadow-sm transition">
                  Download Slip
               </button>
               <span className={`px-4 py-2 rounded-lg text-sm font-bold border ${employee.status === 'Active' ? 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/60' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700'}`}>
                  {employee.status || "Active"}
               </span>
            </div>
        </div>

        {/* METRICS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex items-center gap-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-950/40 rounded-lg text-purple-600 dark:text-purple-400"><BriefcaseIcon className="w-6 h-6" /></div>
                <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-slate-400 uppercase"><T>Active Projects</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{workData.projectCount} Projects</h3>
                </div>
            </div>
            <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex items-center gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600 dark:text-blue-400"><ClipboardDocumentCheckIcon className="w-6 h-6" /></div>
                <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-slate-400 uppercase"><T>Task Performance</T></p>
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{workData.completedTasks} / {workData.assignedTasks.length} Done</h3>
                </div>
            </div>
            <div className="bg-white dark:bg-[#161b22] p-5 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex items-center gap-4">
                <div className="p-3 bg-green-50 dark:bg-green-950/40 rounded-lg text-green-600 dark:text-green-400"><UserCircleIcon className="w-6 h-6" /></div>
                <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-slate-400 uppercase"><T>Joining Date</T></p>
                    {/* 🟢 USE 'date_of_joining' with DD/MM/YYYY formatting */}
                    <h3 className="text-base font-bold text-gray-800 dark:text-white">{formatDate(employee.date_of_joining) || "N/A"}</h3>
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* LEFT COLUMN: Personal Info */}
            <div className="lg:col-span-1 space-y-6">
                <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] p-6">
                    <h3 className="font-bold text-gray-800 dark:text-white mb-4 border-b border-gray-100 dark:border-[#30363d] pb-2"><T>Contact Information</T></h3>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <EnvelopeIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                            <div><p className="text-xs text-gray-500 dark:text-slate-400"><T>Email</T></p><p className="text-sm font-medium text-gray-900 dark:text-slate-200">{employee.email}</p></div>
                        </div>
                        <div className="flex items-center gap-3">
                            <PhoneIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                            <div><p className="text-xs text-gray-500 dark:text-slate-400"><T>Phone</T></p><p className="text-sm font-medium text-gray-900 dark:text-slate-200">{employee.phone || "N/A"}</p></div>
                        </div>
                        {/* 🟢 ADDED LOCATION */}
                        {(employee.city || employee.address) && (
                            <div className="flex items-center gap-3">
                                <MapPinIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400"><T>Location</T></p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-slate-200">{employee.address}, {employee.city}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] p-6">
                    <h3 className="font-bold text-gray-800 dark:text-white mb-4 border-b border-gray-100 dark:border-[#30363d] pb-2"><T>Employment Details</T></h3>
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600 dark:text-slate-400 flex justify-between">
                            <span className="text-gray-500 dark:text-slate-500"><T>Department</T>:</span> 
                            <span className="font-medium text-gray-900 dark:text-white">{employee.department || "General"}</span>
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400 flex justify-between">
                            <span className="text-gray-500 dark:text-slate-500"><T>Type</T>:</span> 
                            <span className="font-medium text-gray-900 dark:text-white">{employee.employment_type || "Full-Time"}</span>
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400 flex justify-between">
                            <span className="text-gray-500 dark:text-slate-500"><T>Gender</T>:</span> 
                            <span className="font-medium text-gray-900 dark:text-white">{employee.gender || "Not Specified"}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: Work & Tasks */}
            <div className="lg:col-span-2">
                <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] flex flex-col h-full">
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-[#30363d] bg-gray-50 dark:bg-[#21262d] rounded-t-xl flex justify-between items-center">
                        <h3 className="font-bold text-gray-800 dark:text-white"><T>Current Assignments</T></h3>
                        <div className="flex gap-3">
                            <button onClick={() => navigate("/admin/Task")} className="text-xs font-bold text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white uppercase"><T>VIEW ALL</T></button>
                        </div>
                    </div>
                    
                    <div className="p-0">
                        {workData.assignedTasks.length === 0 ? (
                            <div className="p-10 text-center text-gray-400 dark:text-slate-500">
                                <p>No tasks assigned to {employee.name}.</p>
                                <button 
                                    onClick={handleAssignTask}
                                    className="mt-2 text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline"
                                >
                                    + Assign Task
                                </button>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-[#30363d]">
                                {workData.assignedTasks.map(t => (
                                    <li key={t.id} className="px-6 py-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-[#21262d]/60 transition">
                                        <div>
                                            <p className="text-sm font-bold text-gray-800 dark:text-white">{t.title}</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">Project: {t.projectName || "General"}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-xs text-gray-500 dark:text-slate-400">Due: {t.dueDate}</span>
                                            <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded 
                                                ${t.status === 'Completed' ? 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-400' : 
                                                  t.status === 'In Progress' ? 'bg-yellow-100 dark:bg-yellow-950/50 text-yellow-700 dark:text-yellow-400' : 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400'}`}>
                                                {t.status}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

        </div>
      </div>
    </>
  );
}